import getMailListViewState from 'mini-mail-list-store/lib/utils/getMailListViewState';
import getSelectedFolderId from 'owa-mail-store/lib/utils/getSelectedFolderId';
import getSelectedTableView from 'mini-mail-list-store/lib/utils/getSelectedTableView';
import listViewStore from 'mini-mail-list-store/lib/store/Store';
import MailFolderTableQuery from 'mini-mail-list-store/lib/store/schema/MailFolderTableQuery';
import MailListEmptyState from './MailListEmptyState';
import MailListHeader from './MailListHeader';
import MailListViewState from 'mini-mail-list-store/lib/store/schema/MailListViewState';
import mailStore from 'owa-mail-store/lib/store/Store';
import NativeAdUserFlight from 'mini-mail-nativead-userchecker/lib/NativeAdUserFlight';
import React from 'react';
import shouldShowAutomaticRepliesBanner from '../utils/mailList/shouldShowAutomaticRepliesBanner';
import TableView from 'mini-mail-list-store/lib/store/schema/TableView';
import UpNextCard from 'mini-mail-list-upnext-view/lib/components/UpNextCard';
import { BINARY_SWITCH } from 'mini-mail-list-store/lib/store/schema/MailHeaderElementToFocus';
import { canShowAdsComponentInListView } from './helpers/canShowAdsComponentInListView';
import { canShowFocusedOtherCampaign } from '../utils/mailList/canShowFocusedOtherCampaign';
import { canShowUpNext } from '../utils/mailList/canShowUpNext';
import { FocusedOtherCampaign } from 'mini-mail-campaign-view/lib/components/FocusedOtherCampaign';
import { format } from 'owa-strings';
import { getAllOnboardingCardsShownStatus } from 'mini-mail-nativead-userchecker/lib/getAllOnboardingCardsShownStatus';
import { getFolderDisplayName } from 'owa-mail-store/lib/utils/getFolderDisplayName';
import { getNativeAdShowStatus } from 'mini-mail-nativead-userchecker/lib/getNativeAdShowStatus';
import { getNativeAdUserFlight } from './helpers/getNativeAdUserFlight';
import { isInSearchView } from 'mini-mail-list-store/lib/selectors/isInSearchView';
import { LazyAutomaticRepliesBanner } from 'mini-mail-automatic-replies-banner-view';
import { measureMailListInitialLoadPerformance } from './helpers/measureMailListInitialLoadPerformance';
import { NativeAdContainer } from 'mini-mail-nativead-view';
import { observer } from 'mobx-react';
import { PlacementId } from 'mini-campaign-bootstrap/lib/schema/PlacementId';
import { PreListBlock } from '../schema/MailListEmptyScreen.types';
import { resetGlobalAdState } from 'mini-mail-nativead-view/lib/utils/getGlobalAdState';
import { RowComponentsHeight, RowComponentsID } from '../utils/mailList/getListItemHeight';
import { shouldShowMailListHeaderForFolderId } from '../utils/mailList/shouldShowMailListHeaderForFolderId';
import { TableQueryType } from 'mini-mail-list-store/lib/store/schema/TableQuery';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import { VirtualizedMailList } from './VirtualizedListView';
import {
    DisplayCustomisationsProvider,
    RichContentFlags,
} from './VirtualizedListView/DisplayCustomisationsProvider';
import {
    ARC_API_CALL_COMPLETED,
    campaignEventEmitter,
} from 'mini-campaign/lib/utils/campaignEventEmitters';

export interface MailListProps {
    isMailListShown: () => boolean;
    tableView: TableView;
}

@observer
export default class MailList extends React.Component<MailListProps, {}> {
    private switchRef = React.createRef<HTMLDivElement>();
    private filterRef = React.createRef<HTMLDivElement>();

    componentDidMount() {
        const tableView = this.props.tableView;
        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            measureMailListInitialLoadPerformance(tableView, mailListLoadState, 'mailListMount');
        }

        campaignEventEmitter.on(
            format(ARC_API_CALL_COMPLETED, PlacementId.MiniOnboardingCardsCarousel),
            ev => {
                const areAllOnboardingCardsShown = getAllOnboardingCardsShownStatus();
                if (ev.isIrisCallSuccess && !ev.isCampaignActive) {
                    if (!areAllOnboardingCardsShown) {
                        /**
                         *If there are no new campaigns, and the previous value for 'allOnboardingCardsShown' is false
                         set the value to true
                        */
                        import('./helpers/setAllOnboardingCardsShownStatus').then(module =>
                            module.setAllOnboardingCardsShownStatus()
                        );
                    }
                }
            }
        );
    }

    componentDidUpdate(prevProps: MailListProps) {
        const tableView = this.props.tableView;
        if (tableView) {
            const mailListLoadState: MailListViewState = getMailListViewState();
            const conversationsOrigin =
                prevProps.tableView?.id !== tableView.id
                    ? 'conversationsInStore'
                    : 'conversationsFetched';
            measureMailListInitialLoadPerformance(
                tableView,
                mailListLoadState,
                conversationsOrigin
            );

            if (tableView.mailHeaderElementToFocus) {
                const element =
                    tableView.mailHeaderElementToFocus === BINARY_SWITCH
                        ? this.switchRef
                        : this.filterRef;
                trySetFocusOnElement(element);
            }
        }
    }

    private renderListViewContent(): JSX.Element {
        const tableView = getSelectedTableView();
        const richContentFlagsForUser: RichContentFlags = {
            shouldShowRSVPUnit: true,
        };

        const showUpNext = listViewStore?.showUpNext || false;
        const showUSQWarningCard = listViewStore?.showUSQWarningCard || false;
        const onboardingCardsHeight = listViewStore?.onboardingCardsHeight || 0;
        return (
            <DisplayCustomisationsProvider
                richContentFlags={richContentFlagsForUser}
                showUpNext={showUpNext}
                showUSQWarningCard={showUSQWarningCard}
                onboardingCardHeight={onboardingCardsHeight}>
                <VirtualizedMailList
                    showNativeAdFormat={getNativeAdUserFlight(tableView)}
                    forwardedRefs={{ switchRef: this.switchRef, filterRef: this.filterRef }}
                    enableSwipeRefresh
                />
            </DisplayCustomisationsProvider>
        );
    }

    private renderEmptyViewContent(mailListViewState: any): JSX.Element {
        const tableView = getSelectedTableView();

        let showNativeAdContainer = NativeAdUserFlight.NoNativeAd;

        if (
            tableView &&
            getNativeAdUserFlight(tableView) !== NativeAdUserFlight.NoNativeAd &&
            mailListViewState === MailListViewState.Empty
        ) {
            showNativeAdContainer = getNativeAdShowStatus();
        }

        if (showNativeAdContainer !== NativeAdUserFlight.NoNativeAd) {
            resetGlobalAdState();
        }

        const { folderTable } = mailStore.folders;
        const selectedFolderId = getSelectedFolderId();
        let folderName: string;

        if (selectedFolderId && folderTable.size > 0) {
            const selectedFolder = folderTable.get(selectedFolderId);

            // Fix for Bug: 34288
            // selectedFolder comes as null some times when pressing hardware back button multiple times.
            // Right now, doing a null check to avoid the error but created a WI 34429 on this.
            folderName = getFolderDisplayName(selectedFolder);
        }

        const isSearchView = isInSearchView();
        const mailListHeaderRef = { switchRef: this.switchRef, filterRef: this.filterRef };
        // The condition is placed based on the condition we have inside the MailListHeader component
        const shouldRenderMailListHeader =
            (isSearchView && tableView?.tableQuery.type === TableQueryType.Search) ||
            shouldShowMailListHeaderForFolderId(selectedFolderId);

        const preListBlocks: PreListBlock[] = [
            {
                component: (
                    <MailListHeader key="MailListHeader" forwardedRefs={mailListHeaderRef} />
                ),
                height: RowComponentsHeight[RowComponentsID.ListHeader],
                shouldRender: shouldRenderMailListHeader,
            },
            {
                component: <NativeAdContainer key="NativeAdContainer" />,
                height: RowComponentsHeight[RowComponentsID.AdBannerContainer],
                shouldRender:
                    canShowAdsComponentInListView() &&
                    showNativeAdContainer === NativeAdUserFlight.PrebidMobileBannerAd,
            },
            {
                component: <LazyAutomaticRepliesBanner key="AutomaticRepliesBanner" />,
                height: RowComponentsHeight[RowComponentsID.AutomaticReplyBanner],
                shouldRender: !isSearchView && shouldShowAutomaticRepliesBanner(tableView),
            },
            {
                component: <FocusedOtherCampaign key="FocusedOtherCampaign" />,
                height: RowComponentsHeight[RowComponentsID.FocusedOtherCampaign],
                shouldRender: !isSearchView && canShowFocusedOtherCampaign(),
            },
            {
                component: <UpNextCard key="UpNextCard" />,
                height: listViewStore?.showUpNext ? RowComponentsHeight[RowComponentsID.UpNext] : 0,
                shouldRender: !isSearchView && canShowUpNext(),
            },
            {
                component: <NativeAdContainer key="NativeAdContainer" />,
                height: RowComponentsHeight[RowComponentsID.AdContainerExp],
                shouldRender:
                    canShowAdsComponentInListView() &&
                    showNativeAdContainer === NativeAdUserFlight.PrebidMobileAllInbox,
            },
        ]
            .map(
                preListBlock =>
                    preListBlock.shouldRender &&
                    ({
                        component: preListBlock.component,
                        height: preListBlock.height,
                    } as PreListBlock)
            )
            .filter(Boolean);

        const tableQuery = tableView?.tableQuery as MailFolderTableQuery;

        return (
            <MailListEmptyState
                folderName={folderName}
                viewFilter={tableQuery?.viewFilter}
                stateType={mailListViewState}
                preListBlocks={preListBlocks}
                focusedViewFilter={tableQuery?.focusedViewFilter}
            />
        );
    }

    render() {
        const mailListViewState = getMailListViewState();
        if (
            [MailListViewState.Loaded, MailListViewState.LoadMoreError].includes(mailListViewState)
        ) {
            return this.renderListViewContent();
        } else {
            return this.renderEmptyViewContent(mailListViewState);
        }
    }
}
